import React from "react";
import { TextileBlock, onTimeHint, type OrderKpis, type TextileSummary } from "./TextileBlock";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, Boxes, Briefcase, Clock, FileClock, Inbox, PackageCheck, Target, Truck, Users } from "lucide-react";
import {
  BarList,
  ColumnChart,
  DataTable,
  SectionCard,
  StatTile,
  TargetBar,
  fmtMoney,
  fmtMonth,
  fmtNum,
  fmtPct,
  type Column,
} from "@/components/reports/ReportWidgets";
import type { ExecRow, HeadRow, LeadSourceRow, StageRow } from "@/types/report";
import { AreaTrendChart } from "@/components/charts/AreaTrendChart";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";

export interface SuperAdminSummary {
  textile: TextileSummary;
  kpis: {
    revenue: number;
    revenue_prev: number;
    target_amount: number;
    target_achieved_pct: number | null;
    pipeline_amount: number;
    weighted_amount: number;
    win_rate_pct: number;
    win_rate_prev: number;
    conversion_pct: number;
    conversion_prev: number;
    new_leads: number;
    quotes_awaiting_approval: number;
  };
  revenue_trend: Array<{ period: string; won_amount: number }>;
  orders: OrderKpis;
  order_trend: Array<{ period: string; order_value: number }>;
  head_leaderboard: HeadRow[];
  top_execs: ExecRow[];
  bottom_execs: ExecRow[];
  pipeline_by_stage: StageRow[];
  leads_by_source: LeadSourceRow[];
  attention: { unassigned_leads: number; untouched_leads_7d: number; overdue_tasks: number; escalated_quotes: number };
  system: { users: { total: number; active: number; suspended: number }; recent_users: any[] };
}

export function AttentionItem({ icon, label, count, to }: { icon: React.ReactNode; label: string; count: number; to: string }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100/90 px-3.5 py-2.5 transition-colors shadow-2xs"
    >
      <span className="text-slate-500">{icon}</span>
      <span className="flex-1 text-xs font-semibold text-slate-700">{label}</span>
      <span className="text-sm font-extrabold tabular-nums text-slate-900">{fmtNum(count)}</span>
      <ArrowRight className="size-3.5 text-slate-400" aria-hidden />
    </Link>
  );
}

export function SuperAdminDashboard({ data }: { data: SuperAdminSummary }) {
  const navigate = useNavigate();
  const k = data.kpis;
  const o = data.orders;

  const headColumns: Column<HeadRow>[] = [
    { key: "name", header: "Sales Head", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "team_size", header: "Team", align: "right" },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
    { key: "conversion_pct", header: "Conversion", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "pipeline_amount", header: "Pipeline", align: "right", render: (r) => fmtMoney(r.pipeline_amount) },
    {
      key: "target_achieved_pct",
      header: "Target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => <TargetBar achieved={r.won_amount} target={r.target_amount} compact />,
    },
  ];

  const execList = (rows: ExecRow[]) =>
    rows.length === 0 ? (
      <p className="text-xs text-muted-foreground py-4 text-center">Not enough executives to rank yet.</p>
    ) : (
      <ul className="divide-y divide-border">
        {rows.map((r) => (
          <li key={r.user_id}>
            <button
              type="button"
              onClick={() => navigate(`/reports/user/${r.user_id}`)}
              className="w-full flex items-center gap-3 py-2 text-left hover:bg-accent/50 rounded-md px-1"
            >
              <span className="flex-1 min-w-0">
                <span className="block text-xs font-medium text-foreground truncate">{r.name}</span>
                <span className="block text-[11px] text-muted-foreground truncate">{r.sales_head || "No Sales Head"}</span>
              </span>
              <span className="text-xs font-semibold tabular-nums text-foreground">{fmtMoney(r.won_amount)}</span>
              <span className="text-[11px] text-muted-foreground w-14 text-right tabular-nums">{fmtPct(r.conversion_pct)} conv.</span>
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <div className="space-y-5">
      <TextileBlock data={data.textile} scope="Across the whole company" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatTile
          label="Orders booked (this month)"
          value={fmtMoney(o.order_value)}
          rawValue={o.order_value}
          previous={o.order_value_prev}
          icon={<PackageCheck className="size-4" />}
          hint={<span>{fmtNum(o.orders_booked)} orders · {fmtMoney(o.dispatched_value)} dispatched</span>}
        />
        <StatTile
          label="Company target"
          value={k.target_achieved_pct === null ? "—" : fmtPct(k.target_achieved_pct)}
          icon={<Target className="size-4" />}
          hint={k.target_amount ? <TargetBar achieved={k.revenue} target={k.target_amount} /> : <Link to="/targets" className="text-primary hover:underline">Set targets</Link>}
        />
        <StatTile
          label="Open enquiries"
          value={fmtMoney(k.pipeline_amount)}
          icon={<Briefcase className="size-4" />}
          hint={<span>{fmtMoney(k.weighted_amount)} weighted · {fmtPct(k.conversion_pct)} lead conversion</span>}
        />
        <StatTile
          label="Dispatch on time"
          value={o.on_time_pct === null ? "—" : fmtPct(o.on_time_pct)}
          icon={<Truck className="size-4" />}
          hint={<span>{onTimeHint(o)}</span>}
        />
        <StatTile
          label="Order book to dispatch"
          value={fmtMoney(o.order_book_value)}
          icon={<Boxes className="size-4" />}
          hint={<Link to="/orders" className="text-primary hover:underline">{fmtNum(o.order_book_orders)} open orders</Link>}
        />
        <StatTile label="New leads (this month)" value={fmtNum(k.new_leads)} icon={<Inbox className="size-4" />} />
        <StatTile
          label="Quotes awaiting approval"
          value={fmtNum(k.quotes_awaiting_approval)}
          icon={<FileClock className="size-4" />}
          hint={<Link to="/quotes" className="text-primary hover:underline">Open quotes</Link>}
        />
        <StatTile
          label="Active users"
          value={fmtNum(data.system.users?.active || 0)}
          icon={<Users className="size-4" />}
          hint={<Link to="/users" className="text-primary hover:underline">{fmtNum(data.system.users?.total || 0)} total</Link>}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard title="Orders booked" description="Order value per month before GST, last 12 months" className="xl:col-span-2">
          <AreaTrendChart
            data={(data.order_trend || []).map((r) => ({
              period: r.period,
              label: fmtMonth(r.period),
              value: r.order_value,
            }))}
            formatValue={(v) => fmtMoney(v)}
            color="#2563eb"
            height={210}
          />
        </SectionCard>

        <SectionCard title="Target achievement" description="Won value against the company's monthly target">
          <RadialTargetGauge
            achieved={k.revenue}
            target={k.target_amount}
            formatMoney={(v) => fmtMoney(v)}
            size={180}
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <SectionCard title="Needs attention" description="Across the whole company" className="xl:col-span-1">
          <div className="space-y-2">
            <AttentionItem icon={<Inbox className="size-4" />} label="Unassigned leads in pool" count={data.attention.unassigned_leads} to="/deals" />
            <AttentionItem icon={<Clock className="size-4" />} label="Leads untouched for 7+ days" count={data.attention.untouched_leads_7d} to="/reports?tab=ageing" />
            <AttentionItem icon={<AlertTriangle className="size-4" />} label="Overdue tasks" count={data.attention.overdue_tasks} to="/tasks" />
            <AttentionItem icon={<FileClock className="size-4" />} label="Escalated quote approvals" count={data.attention.escalated_quotes} to="/quotes" />
          </div>
        </SectionCard>

        <SectionCard
          title="Sales Head leaderboard"
          description="This month, each head's whole team"
          action={<Link to="/reports?tab=heads" className="text-xs text-primary hover:underline">Full report</Link>}
          className="xl:col-span-2"
        >
          <DataTable
            columns={headColumns}
            rows={data.head_leaderboard}
            rowKey={(r) => r.user_id}
            onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
            emptyMessage="No Sales Heads yet."
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Pipeline value by stage"
          description="Distribution of open deals"
          action={<Link to="/reports?tab=pipeline" className="text-xs text-primary hover:underline">Details</Link>}
        >
          <DonutPieChart
            data={data.pipeline_by_stage.map((s) => ({
              label: s.stage_name,
              value: s.amount,
              sublabel: `${s.deals} deals`,
            }))}
            formatValue={(v) => fmtMoney(v)}
            centerValue={fmtMoney(k.pipeline_amount)}
            centerLabel="Pipeline"
            donut={true}
            onSliceClick={() => navigate("/deals")}
          />
        </SectionCard>

        <SectionCard
          title="Leads by source"
          description="Source channel breakdown (last 90 days)"
          action={<Link to="/reports?tab=sources" className="text-xs text-primary hover:underline">Details</Link>}
        >
          <DonutPieChart
            data={data.leads_by_source.map((s) => ({
              label: s.source_name,
              value: s.leads,
              sublabel: `${fmtPct(s.conversion_pct)} converted`,
            }))}
            formatValue={(v) => `${fmtNum(v)} leads`}
            centerValue={fmtNum(data.leads_by_source.reduce((a, b) => a + (b.leads || 0), 0))}
            centerLabel="Total Leads"
            donut={true}
            onSliceClick={() => navigate("/leads")}
          />
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Top Sales Executives" description="By revenue won this month">
          {execList(data.top_execs)}
        </SectionCard>
        <SectionCard title="Needs support" description="Lowest revenue won this month">
          {execList(data.bottom_execs)}
        </SectionCard>
      </div>
    </div>
  );
}
